import React from "react";

import ExternalButton from "../../components/BaseButton/ExternalButton";
import LinkButton from "../../components/BaseButton/LinkButton";
import MainLogo from "../../components/MainLogo";
import { useAppData } from "../../data/AppDataProvider";
import styles from "./Menu.module.css";

type MenuProps = {
    shouldShowInstallButton: boolean;
    onInstallClick: () => void;
};

type AppearStyle = React.CSSProperties & {
    "--appear-delay": string;
};

type MenuData = {
    mainPageData: {
        expire?: string | null;
    };
    mainPageStatus: {
        isLoading: boolean;
        error?: string;
    };
};

function parseExpireDate(value?: string | null): Date | null {
    if (!value || typeof value !== "string") {
        return null;
    }

    const match = value.match(/^(\d{2})\.(\d{2})\.(\d{2}|\d{4})$/);
    if (!match) {
        return null;
    }

    const day = Number(match[1]);
    const month = Number(match[2]) - 1;
    const rawYear = Number(match[3]);
    const year = match[3].length === 2 ? 2000 + rawYear : rawYear;
    const parsedDate = new Date(year, month, day, 23, 59, 59, 999);

    if (
        Number.isNaN(parsedDate.getTime())
        || parsedDate.getFullYear() !== year
        || parsedDate.getMonth() !== month
        || parsedDate.getDate() !== day
    ) {
        return null;
    }

    return parsedDate;
}

function getAppearStyle(): AppearStyle  {
    return {
        "--appear-delay": `${(0.08 + Math.random() * 0.18).toFixed(3)}s`,
    };
}

export default function Menu({ shouldShowInstallButton, onInstallClick }: MenuProps) {
    const { mainPageData, mainPageStatus } = useAppData() as MenuData;
    const { isLoading, error } = mainPageStatus;

    const tariffLabel = "от 150 ₽";
    const expireDate = parseExpireDate(mainPageData.expire);
    const hasActiveSubscription = Boolean(expireDate && expireDate >= new Date());
    const expireLabel = !isLoading && !hasActiveSubscription ? "истек" : "до";
    const subscriptionStatus = isLoading
        ? "загрузка"
        : hasActiveSubscription
            ? "оплачен"
            : "не оплачен";
    const expireText = isLoading ? "..." : mainPageData.expire || "нет подписки";
    const statusBadgeClassName = [
        styles.statusBadge,
        !isLoading && !hasActiveSubscription ? styles.statusBadgeDanger : "",
    ].filter(Boolean).join(" ");

    return (
        <>

            <div className="flex-center h100">
                <MainLogo />
            </div>

            <div className="flex-col gap1">
                <div className={`${styles.statusRow} menu-appear`} style={getAppearStyle()}>
                    <span className={`span-main-item grey ${styles.statusText}`}>
                        <span className={statusBadgeClassName} aria-hidden="true">
                            <svg className={styles.statusIcon} viewBox="0 0 24 24">
                                <use href="/svg/calendar.svg#calendar"></use>
                            </svg>
                        </span>
                        VPN{" "}
                        <span
                            id="expire_pay"
                            className={isLoading ? "grey" : hasActiveSubscription ? "srok-oplaty-green" : "srok-oplaty-red"}
                        >
                            <b>{subscriptionStatus}</b>
                        </span>
                    </span>

                    <span className={`span-main-item grey ${styles.expireText}`}>
                        {expireLabel} <u><span id="expire_date">{expireText}</span></u>
                    </span>
                </div>

                {error ? (
                    <div className="gray-block pad1 shadow-1 menu-appear" style={getAppearStyle()}>
                        <span>{error}</span>
                    </div>
                ) : null}

                <div className={`flex-col ${styles.menuActions}`}>
                    <LinkButton
                        icon="icon-coins"
                        to="/tariffs"
                        className="menu-appear"
                        style={getAppearStyle()}
                    >
                        <div style={{display: "flex", justifyContent: "space-between"}}>
                            <span>Купить доступ</span>
                            <span style={{ color: "var(--color-accent)" }}>{tariffLabel}</span>
                        </div>
                    </LinkButton>

                    <LinkButton
                        icon="key.svg"
                        to="/devices"
                        className="menu-appear"
                        style={getAppearStyle()}
                    >
                        <span>Подключить устройство</span>
                    </LinkButton>

                    <div style={{display: "flex", gap: "inherit"}}>
                        <LinkButton
                            icon="icon-user"
                            to="/cabinet"
                            className={`menu-appear`}
                            style={{flex: 1, ...getAppearStyle()}}
                        >
                            <span>Кабинет</span>
                        </LinkButton>

                        <ExternalButton
                            icon="icon-support"
                            href="https://t.me/sodavpn_support"
                            className={`menu-appear`}
                            style={{flex: 1, ...getAppearStyle()}}
                        >
                            <span>Помощь</span>
                        </ExternalButton>
                    </div>
                </div>
            </div>
        </>
    );
}
